import {JSONSchema7} from "json-schema";
import * as React from 'react';
import {useEffect, useRef, useState} from "react";
import {withNamespaces, WithNamespaces} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {FormField, Message} from "semantic-ui-react";
import CodeEditor from "../../../../core/components/CodeEditor/CodeEditor";
import {toggleSidebarEditor} from "../../../../Layout/actions/commands";
import {makeEditorStateByNameSelector} from "../../../../Layout/selectors/sidebar-editor";
import {useElementsTree} from "../../../hooks/useElementsTree";
import {BoardId} from "../../../model/Board";
import {WizardContext} from "../../../service/cody-wizard/context/wizard-context";
import {UiMetadata} from "../../../service/cody-wizard/ui/get-ui-metadata";
import {parse, stringify} from "../../../service/json/json-comment-wrapper";
import EditorToolbar from "./EditorToolbar";
import {JSONSchemaWithMarkdown, MarkdownDescription} from "./Suggestion/markdown-description";
import {
  getPossibleCommands,
  getPossibleForms,
  getPossibleListVOs, getPossiblePages,
  getPossibleViews
} from "./Typeahead/elements-tree-lookup";
import {makeRuleThenPropMapping} from "./Validation/rule-engine/prop-mapping";
import {
  makeActionsUiSchema,
  makeCommandButtonUiSchema, makeCommandComponents,
  makeCommandFormUiSchema,
  makeTableUiSchema,
  makeUiSchema
} from "./Validation/ui-schema";
import {makeCssSchemaDefinitions} from "./Validation/css-schema";
import {JEXL_WIKI_MARKDOWN, makeJexlExpression} from "./Validation/rule-engine/jexl-expressions";

interface OwnProps {
  boardId: BoardId;
  uiConfig: UiMetadata;
  onUiConfigChanged: (config: UiMetadata) => void;
  autofocus?: boolean;
  readonly?: boolean;
  sessionId?: string;
  ctx: WizardContext;
  toolbarSlot?: JSX.Element;
}

type PageContentEditorProps = OwnProps & WithNamespaces;

const unsavedSessions: Record<string, string> = {};

const PageContentEditor = (props: PageContentEditorProps) => {
  const elementsTree = useElementsTree(props.boardId);
  const codeEditorRef = useRef<CodeEditor|null>(null);
  const [editorHasFocus, setEditorHasFocus] = useState(false);
  const [invalidJson, setInvalidJson] = useState(false);
  const editorState = useSelector(makeEditorStateByNameSelector('page-content'));
  const dispatch = useDispatch();

  const {commands, views} = props.uiConfig;

  let value = stringify({commands, views}, null, 2);

  useEffect(() => {
    if (!codeEditorRef.current) {
      return;
    }

    let isInvalidJson = false;

    if(props.sessionId && typeof unsavedSessions[props.sessionId] === "string") {
      value = unsavedSessions[props.sessionId];
      isInvalidJson = true;
    }

    codeEditorRef.current!.initializeModel({
      fileId: `${props.sessionId}-ui-page-content.json`,
      language: "json",
      value,
      schema: makePageContentSchema(
        getPossibleCommands(elementsTree),
        getPossibleViews(elementsTree),
        getPossibleListVOs(elementsTree),
        getPossibleForms(elementsTree),
        getPossiblePages(elementsTree)
      ),
    });

    setInvalidJson(isInvalidJson);
  }, [props.sessionId, value]);

  useEffect(() => {
    if(props.autofocus && codeEditorRef.current) {
      codeEditorRef.current.focus();
    }
  }, [props.autofocus])

  const propagateChanges = (editorStr: string, silentError?: boolean): boolean => {
    if(editorStr === '') {
      props.onUiConfigChanged({...props.uiConfig, commands: [], views: []});

      if(props.sessionId && unsavedSessions[props.sessionId]) {
        delete unsavedSessions[props.sessionId];
      }
      return true;
    }

    try {
      const changedConfig = parse(editorStr);
      props.onUiConfigChanged({...props.uiConfig, ...changedConfig});

      if(props.sessionId && unsavedSessions[props.sessionId]) {
        delete unsavedSessions[props.sessionId];
      }

      return true;
    } catch (e) {
      console.error("[CodyWizard] UI Config Editor invalid JSON: ", e);
      if(!silentError) {
        setInvalidJson(true);
      }
      return false;
    }
  }

  return <FormField>
    {invalidJson && <Message error={true} size="small" content={props.t('insp.cody_wizard.schema_editor.invalid_json') as string} icon="warning" style={{display: 'flex'}}/>}

    <EditorToolbar
      leftSideSlot={props.toolbarSlot}
      expanded={editorState.expanded}
      double={editorState.double}
      onExpandChanged={(expand, double) => dispatch(toggleSidebarEditor('page-content', expand, !!double))}/>
    <CodeEditor
      containerId={`cody-ui-page-content-editor-${props.sessionId}`}
      ref={codeEditorRef}
      repairJson={true}
      options={{
        fontSize: 12,
        folding: true,
        glyphMargin: false,
        lineNumbers: true,
        lineDecorationsWidth: 3,
        minimap: {
          enabled: false
        },
        formatOnPaste: true,
        scrollBeyondLastLine: false,
        mouseWheelZoom: true,
        automaticLayout: true,
        scrollbar: {
          alwaysConsumeMouseWheel: false
        },
        readOnly: props.readonly,
        fixedOverflowWidgets: true,
      }}
      className={"code editor" + (editorHasFocus ? ' focus' : '') + (editorState.expanded ? ' expanded' : '')  + (editorState.double ? ' double' : '')}
      onBlur={() => {
        if(codeEditorRef.current) {
          const changedUiConfigsStr = codeEditorRef.current.retrievePayload();

          const validJson = propagateChanges(changedUiConfigsStr);

          setEditorHasFocus(false);

          if(!validJson && props.sessionId) {
            unsavedSessions[props.sessionId] = changedUiConfigsStr;
          }
        }
      }}
      onFocus={() => {
        setEditorHasFocus(true);
      }}
    />
  </FormField>
};

export default withNamespaces()(PageContentEditor);


const makePageContentSchema = (possibleCommands: string[], possibleViews: string[], possibleListVOs: string[], possibleForms: string[], possiblePages: string[]): string => {
  const viewsSchema: JSONSchemaWithMarkdown = {
    type: "array",
    markdownDescription: "List of View Components on the page.\n\nEach queryable Information becomes one component on the page. If you want to load the Information but not show it (e.g. to access it in a Jexl Expression), you can use:\n\n```{ \"view\": \"MyInformation\", \"hidden\": true }```",
    items: {
      oneOf: [
        {type: "string", enum: possibleViews, markdownDescription: "Name of the view"} as JSONSchema7,
        {
          type: "object",
          additionalProperties: false,
          required: ["view"],
          markdownDescription: "View configuration",
          properties: {
            view: {type: "string", enum: possibleViews, markdownDescription: "Name of the view"},
            hidden: {type: "boolean", default: true, markdownDescription: "Is the information visible on the page?"},
            'hidden:expr': makeJexlExpression(`Is the information visible on the page?\n\nType: ${JEXL_WIKI_MARKDOWN}`),
            type: {
              type: "string",
              enum: ["auto", "form", "state", "table"],
              default: "auto",
              markdownDescription: "Type of the view. In most cases Cody will automatically detect the correct view type, but if you want to show information in an editable form, use the 'form' type."
            },
            'type:expr': makeJexlExpression(`Dynamically set the view type via ${JEXL_WIKI_MARKDOWN}`),
            loadState: {
              type: "boolean",
              default: true,
              markdownDescription: "Cody will try to load the information when the user navigates to the page. You can disable the behavior e.g. when rendering a form to create new information."
            },
            props: {
              type: "object",
              additionalProperties: true,
              properties: {
                container: {$ref: "#/definitions/mui-container"}
              }
            },
            'props:expr': {$ref: "#/definitions/rule_then_prop_mapping"},
            data: {$ref: "#/definitions/rule_then_prop_mapping"},
            uiSchema: {
              type: "object"
            }
          },
          if: {
            properties: {
              type: {"const": "form"},
            },
            required: ["type"]
          },
          then: {
            properties: {
              uiSchema: {
                ...makeUiSchema({...makeCommandFormUiSchema(possiblePages)}, possibleListVOs, possibleCommands, possiblePages, possibleListVOs, possibleForms, {container: {$ref: "#/definitions/mui-container"}, ...makeActionsUiSchema(possibleCommands, possiblePages, possibleListVOs, possibleForms)}),
                markdownDescription: "Override or add uiSchema properties of the view"
              }
            }
          },
          else: {
            properties: {
              uiSchema: {
                ...makeUiSchema({}, possibleListVOs, possibleCommands, possiblePages, possibleListVOs, possibleForms, {container: {$ref: "#/definitions/mui-container"}, ...makeActionsUiSchema(possibleCommands, possiblePages, possibleListVOs, possibleForms)}),
                markdownDescription: "Override or add uiSchema properties of the view"
              }
            }
          }
        } as JSONSchema7
      ]
    }
  };

  const schema: JSONSchema7 = {
    type: "object",
    additionalProperties: false,
    required: ["commands", "views"],
    properties: {
      commands: makeCommandComponents(possibleCommands, possiblePages, possibleListVOs, possibleForms),
      views: viewsSchema
    },
    definitions: {
      ...makeRuleThenPropMapping(),
      ...makeCssSchemaDefinitions()
    }
  }

  return stringify(schema);
}
